





























































import Vue from 'vue'
import Autolinker from 'autolinker'
import debounce from 'lodash.debounce'
import Icon from '@/components/Icon/Icon.vue'
import Collapse from '@/components/Collapse/Collapse.vue'
import HintCardField from './HintCardField.vue'

interface Data {
  title: string
  snippet: string
  content: string
  isEditMode: boolean
}

interface Methods {
  handleToggleHint: () => void
  handleToggleEdit: () => void
  updateHint: () => void
  handleDelete: () => void
  handleValidateText: () => void
  debouncedUpdateHint: () => void
}

interface Computed {
  titlePlaceholder: string
  snippetPlaceholder: string
  contentPlaceholder: string
  contentWithHighlightedLinks: string
}

interface Props {
  item: HintItem
  isReadonly: boolean
  isCollapsed: boolean
  isSaveInProgress: boolean
  hasShowIndicator: boolean
  saveErrorMessage: string | false
  maxTitleLength: number
  maxSnippetLength: number
}

export interface HintItem {
  id: string
  title: string
  snippet: string
  content: string
  views?: number
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    Icon,
    Collapse,
    HintCardField,
  },
  props: {
    item: {
      required: true,
    },
    isReadonly: {
      default: () => false,
    },
    isCollapsed: {
      default: () => true,
    },
    isSaveInProgress: {
      default: () => false,
    },
    hasShowIndicator: {
      default: () => true,
    },
    saveErrorMessage: {
      default: () => false,
    },
    maxTitleLength: {
      default: () => 60,
    },
    maxSnippetLength: {
      default: () => 120,
    },
  },
  data() {
    return {
      title: this.item.title ?? '',
      snippet: this.item.snippet ?? '',
      content: this.item.content ?? '',
      isEditMode: false,
    }
  },
  computed: {
    titlePlaceholder() {
      return this.$tc('common.title')
    },
    snippetPlaceholder() {
      return this.$tc('common.description')
    },
    contentPlaceholder() {
      return this.$tc('common.text')
    },
    contentWithHighlightedLinks() {
      if (!this.content) {
        return ''
      }
      return Autolinker.link(this.content, { className: 'content-field-link' })
    },
  },
  methods: {
    handleToggleHint() {
      this.$emit('on-toggle', this.item)
    },
    handleToggleEdit() {
      this.isEditMode = !this.isEditMode

      const isNeedCollapse = this.isEditMode === this.isCollapsed
      if (isNeedCollapse) {
        this.handleToggleHint()
      }
    },
    updateHint() {
      const newItem: HintItem = {
        id: this.item.id,
        title: this.title,
        snippet: this.snippet,
        content: this.content,
      }
      this.$emit('on-update', newItem)
    },
    handleDelete() {
      this.$emit('on-delete', this.item)
    },
    handleValidateText() {
      this.$emit('on-validate')
    },
    debouncedUpdateHint() {
      return null
    },
  },
  watch: {
    title() {
      this.debouncedUpdateHint()
    },
    snippet() {
      this.debouncedUpdateHint()
    },
    content() {
      this.debouncedUpdateHint()
    },
    item() {
      if (this.isReadonly) {
        this.content = this.item.content
      }
    },
  },
  created() {
    if (!this.isReadonly) {
      this.debouncedUpdateHint = debounce(this.updateHint, 500)
    }
  },
})
